// Invoice uploads page — full page, dedicated to managing AusPost invoice
// uploads (CSV is the canonical per-parcel source; PDF is stored as the
// human-readable attachment). The recent-uploads list links into the
// detail page for each invoice's line-level results.

import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Loader2, Upload, FileText, ChevronRight, AlertCircle, CheckCircle2,
  Calendar,
} from 'lucide-react'
import {
  uploadAuspostInvoice, listAuspostInvoices,
} from '../../lib/api'
import { ToastContainer, useToast } from '../../components/Toast'
import AuspostTabs from './AuspostTabs'


function fmt$(v) {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency', currency: 'AUD', minimumFractionDigits: 2,
  }).format(v ?? 0)
}
function fmtDate(s) {
  if (!s) return '—'
  const d = new Date(s)
  return isNaN(d.getTime()) ? s : d.toLocaleDateString('en-AU', {
    day: 'numeric', month: 'short', year: 'numeric',
  })
}
function fmtDateTime(s) {
  if (!s) return '—'
  const d = new Date(s)
  return isNaN(d.getTime()) ? s : d.toLocaleString('en-AU', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}


export default function AuspostInvoices() {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-800">AusPost Reconciliation</h2>
        <p className="text-sm text-slate-500 mt-0.5">
          Upload eParcel billing-detail CSVs, MyPost Business / StarTrack
          TransactionSummary CSVs, or tax-invoice PDFs. The CSVs drive
          per-parcel reconciliation; the PDF is stored as the human-readable
          attachment. Variances in already-billed months defer to the
          current open billing period.
        </p>
      </div>

      <AuspostTabs />

      <UploadCard />
      <RecentUploads />
    </div>
  )
}


// ─── Upload card ───────────────────────────────────────────────────────────

function UploadCard() {
  const queryClient = useQueryClient()
  const { toasts, addToast, removeToast } = useToast()
  const [file, setFile] = useState(null)
  const [dragOver, setDragOver] = useState(false)

  const uploadMut = useMutation({
    mutationFn: (f) => uploadAuspostInvoice(f),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['auspost'] })
      if (data.status === 'duplicate') {
        addToast({
          type: 'info',
          message: `Invoice #${data.invoice_number} was already ingested. ${data.message || ''}`,
          duration: 8000,
        })
      } else {
        const c = data.counts || {}
        const bits = [
          c.charged_open_period ? `${c.charged_open_period} charged (open period)` : null,
          c.discrepancies_filed ? `${c.discrepancies_filed} deferred to current month` : null,
          c.pending_review      ? `${c.pending_review} need review`                  : null,
          c.unchanged           ? `${c.unchanged} already at target`                 : null,
          c.no_ss_match         ? `${c.no_ss_match} no SS match`                     : null,
        ].filter(Boolean).join(' · ')
        addToast({
          type: 'success',
          message:
            `Invoice #${data.invoice_number} (${data.source_type?.toUpperCase()}) ingested. ` +
            `${data.lines_inserted ?? 0} lines.\n${bits || 'No reconciliation actions.'}`,
          duration: 10_000,
        })
      }
      setFile(null)
    },
    onError: (err) => addToast({
      type: 'error',
      message: err.response?.data?.detail || err.message || 'Upload failed',
      duration: 10_000,
    }),
  })

  const onPick = (f) => {
    if (!f) return
    const name = (f.name || '').toLowerCase()
    if (!name.endsWith('.csv') && !name.endsWith('.pdf')) {
      addToast({ type: 'error', message: 'File must be a CSV or PDF.', duration: 6000 })
      return
    }
    setFile(f)
  }

  return (
    <div className="card">
      <ToastContainer toasts={toasts} onClose={removeToast} />
      <div className="px-5 py-4 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded bg-blue-50 flex items-center justify-center">
            <Upload size={16} className="text-blue-600" />
          </div>
          <div>
            <div className="font-semibold text-slate-800">Upload a new invoice</div>
            <div className="text-xs text-slate-500">
              eParcel CSV (112 cols) <span className="text-slate-300">·</span> MyPost / StarTrack CSV (6 cols) <span className="text-slate-300">·</span> PDF
            </div>
          </div>
        </div>
      </div>

      <div className="p-5 space-y-3">
        <label
          htmlFor="auspost-invoice-upload"
          onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault()
            setDragOver(false)
            onPick(e.dataTransfer.files?.[0])
          }}
          className={[
            'flex flex-col items-center justify-center gap-2 px-6 py-10 border-2 border-dashed rounded-lg text-center cursor-pointer transition-colors',
            dragOver
              ? 'border-blue-400 bg-blue-50/50 text-blue-700'
              : 'border-slate-300 bg-slate-50/50 text-slate-500 hover:bg-slate-50',
          ].join(' ')}
        >
          {file ? (
            <>
              <FileText size={28} className="text-blue-500" />
              <div className="font-medium text-slate-800">{file.name}</div>
              <div className="text-xs text-slate-400">
                {(file.size / 1024 / 1024).toFixed(2)} MB · {file.type || 'unknown type'}
              </div>
            </>
          ) : (
            <>
              <Upload size={28} className={dragOver ? 'text-blue-500' : 'text-slate-400'} />
              <div className="text-sm font-medium">
                {dragOver ? 'Drop the file to upload' : 'Drop a CSV or PDF here, or click to browse'}
              </div>
              <div className="text-xs text-slate-400">Files up to 25 MB</div>
            </>
          )}
          <input
            id="auspost-invoice-upload"
            type="file"
            accept=".csv,.pdf,text/csv,application/pdf"
            onChange={(e) => onPick(e.target.files?.[0])}
            className="hidden"
          />
        </label>

        <div className="flex items-center gap-2">
          <button
            onClick={() => file && uploadMut.mutate(file)}
            disabled={!file || uploadMut.isPending}
            className="btn-primary text-sm"
          >
            {uploadMut.isPending
              ? <><Loader2 size={14} className="animate-spin" /> Uploading & reconciling…</>
              : <><Upload size={14} /> Upload & reconcile</>}
          </button>
          {file && !uploadMut.isPending && (
            <button onClick={() => setFile(null)} className="text-sm text-slate-500 hover:text-slate-800 px-2">
              Clear
            </button>
          )}
          <div className="ml-auto text-xs text-slate-400">
            Same SHA = no double-ingest. Locked periods defer to the current open month.
          </div>
        </div>
      </div>
    </div>
  )
}


// ─── Recent uploads list ───────────────────────────────────────────────────

function RecentUploads() {
  const { data: invoices, isLoading } = useQuery({
    queryKey: ['auspost', 'invoices'],
    queryFn:  () => listAuspostInvoices({ limit: 50 }),
    refetchInterval: 60_000,
    staleTime: 30_000,
  })

  return (
    <div className="card overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
        <div className="font-semibold text-slate-800">Recent uploads</div>
        <div className="text-xs text-slate-400">
          {invoices?.length ? `${invoices.length} most recent` : ''}
        </div>
      </div>

      {isLoading ? (
        <div className="px-5 py-10 flex items-center gap-2 justify-center text-slate-400 text-sm">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      ) : !invoices?.length ? (
        <div className="px-5 py-10 text-center">
          <FileText size={28} className="text-slate-300 mx-auto" />
          <div className="mt-2 text-sm font-medium text-slate-600">No uploads yet</div>
          <div className="text-xs text-slate-400">Upload your first invoice above to see it here.</div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-xs text-slate-500 uppercase tracking-wider">
              <tr>
                <th className="px-4 py-2.5 text-left">Invoice</th>
                <th className="px-4 py-2.5 text-left">Uploaded</th>
                <th className="px-4 py-2.5 text-right">Total inc-GST</th>
                <th className="px-4 py-2.5 text-right">Lines (w/ article)</th>
                <th className="px-4 py-2.5 text-left">Recon outcome</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {invoices.map((inv) => (
                <InvoiceRow key={inv.id} inv={inv} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function InvoiceRow({ inv }) {
  const c = inv.recon_counts || {}
  const isPdf = inv.source_type === 'pdf'
  return (
    <tr className="hover:bg-slate-50">
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <FileText size={14} className={isPdf ? 'text-rose-400' : 'text-emerald-500'} />
          <div>
            <div className="font-mono text-slate-700">#{inv.invoice_number}</div>
            <div className="text-[10px] text-slate-400">
              {inv.source_type?.toUpperCase()}
              {inv.billing_date && <> · billed {fmtDate(inv.billing_date)}</>}
              {inv.period_end_date && <> · period ends {fmtDate(inv.period_end_date)}</>}
            </div>
          </div>
        </div>
      </td>
      <td className="px-4 py-3 text-xs text-slate-600">
        <div className="flex items-center gap-1.5">
          <Calendar size={11} className="text-slate-400" />
          {fmtDateTime(inv.uploaded_at)}
        </div>
      </td>
      <td className="px-4 py-3 text-right tabular-nums text-slate-700 font-medium">
        {fmt$(inv.total_amount_inc_gst)}
      </td>
      <td className="px-4 py-3 text-right tabular-nums text-slate-700">
        {inv.line_count_with_article ?? 0}
        <span className="text-slate-400 ml-1">/ {inv.line_count_total ?? 0}</span>
      </td>
      <td className="px-4 py-3 text-xs">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
          {c.charged_open_period > 0 && (
            <span className="inline-flex items-center gap-1 text-emerald-600">
              <CheckCircle2 size={11} /> {c.charged_open_period} charged
            </span>
          )}
          {c.discrepancies_filed > 0 && (
            <span className="inline-flex items-center gap-1 text-amber-600">
              <Calendar size={11} /> {c.discrepancies_filed} deferred
            </span>
          )}
          {c.pending_review > 0 && (
            <span className="inline-flex items-center gap-1 text-red-600">
              <AlertCircle size={11} /> {c.pending_review} review
            </span>
          )}
          {c.unchanged > 0 && (
            <span className="text-slate-400">{c.unchanged} unchanged</span>
          )}
          {c.no_ss_match > 0 && (
            <span className="text-slate-400">{c.no_ss_match} no SS match</span>
          )}
          {Object.keys(c).length === 0 && (
            <span className="text-slate-400 italic">PDF — header only</span>
          )}
        </div>
      </td>
      <td className="px-4 py-3 text-right">
        <Link
          to={`/admin/auspost/invoices/${inv.id}`}
          className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800"
        >
          View <ChevronRight size={14} />
        </Link>
      </td>
    </tr>
  )
}
